import React, { useState, useRef, useContext, useEffect } from "react";

import { Typography } from "@mui/material";
import { useMapColorScheme } from "@rubi/ui";
import {
  Map,
  MapCameraChangedEvent,
  Marker,
  useApiIsLoaded,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";

import { CameraRegistryContext } from "@/components/canvas-registry/AddRegistryPage/context";
import { ICoordinates, IPlace } from "@/types";

const MapSearch: React.FC<any> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
}) => {
  const mapColorScheme = useMapColorScheme();
  const apiIsLoaded = useApiIsLoaded();
  const { police, clearCameras } = useContext(CameraRegistryContext);
  if (!police || !clearCameras) {
    return <></>;
  }

  const defaultAddress = () => {
    return values.googleMapsPlace?.formatted_address
      ? values.googleMapsPlace.formatted_address?.includes(
          values.googleMapsPlace.name ? values.googleMapsPlace.name : "",
        )
        ? values.googleMapsPlace.formatted_address
        : values.googleMapsPlace.name +
          " - " +
          values.googleMapsPlace.formatted_address
      : "";
  };

  const [location, setLocation] = useState<ICoordinates>(
    values.googleMapsPlace?.geometry
      ? values.googleMapsPlace?.geometry
      : police.mapCenter,
  );
  const [zoom, setZoom] = useState(values.googleMapsPlace ? 16 : 12);
  const [showUnit, setShowUnit] = useState(
    values?.googleMapsPlace?.address_components &&
      !values?.googleMapsPlace?.address_components?.some(
        (component: google.maps.GeocoderAddressComponent) =>
          component.types.includes("subpremise"),
      ),
  );

  // const [error, setError] = useState<string | null>(null);
  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [addressText, setAddressText] = useState(defaultAddress());
  const placesLibrary = useMapsLibrary("places");

  useEffect(() => {
    setLocation(
      values.googleMapsPlace?.geometry
        ? values.googleMapsPlace?.geometry
        : police.mapCenter,
    );
    setAddressText(defaultAddress());

    setShowUnit(
      !values.googleMapsPlace?.address_components?.some((component) =>
        component.types.includes("subpremise"),
      ),
    );

    if (values.googleMapsPlace?.geometry) {
      setZoom(16);
    }
  }, [values.googleMapsPlace]);

  // Kept apart from `location`: `location` is where the pin sits, this is where
  // the map is looking. Panning must not drag the pin, and choosing an address
  // must recentre the map.
  const [camera, setCamera] = useState({ center: location, zoom });

  useEffect(() => {
    setCamera({ center: location, zoom });
  }, [location, zoom]);

  const handlePlacesChanged = () => {
    const places = searchBoxRef.current?.getPlaces();
    if (places && places.length > 0) {
      const place = places[0];
      if (place.formatted_address?.includes(place.name ? place.name : ""))
        setAddressText(place.formatted_address);
      else setAddressText(place.name + " - " + place.formatted_address);

      let lat = 0;
      let lng = 0;
      if (place?.geometry?.location) {
        lat = place.geometry?.location.lat();
        lng = place.geometry?.location.lng();
      }

      if (lat && lng) {
        const selectedLocation = { lat, lng };

        const formattedPlace: IPlace = {
          address_components: place.address_components
            ? [...place.address_components]
            : [],
          formatted_address: place.formatted_address
            ? place.formatted_address
            : "",
          geometry: selectedLocation,
          name: place.name ? place.name : "",
          url: place.url ? place.url : "",
          types: place.types ? [...place.types] : [],
          place_id: place.place_id ? place.place_id : "",
        };
        setFieldValue("googleMapsPlace", formattedPlace);

        setShowUnit(
          !place.address_components?.some((component) =>
            component.types.includes("subpremise"),
          ),
        );
        clearCameras();
        setLocation(selectedLocation);
        setZoom(16);
      }
    }
  };

  // The listener is attached once and calls through a ref, so the SearchBox is
  // not torn down and rebuilt on every keystroke while the handler still sees
  // current props. Bounds come from lat/lng primitives rather than the literal
  // above, whose identity changes every render.
  const placesChangedRef = useRef(handlePlacesChanged);
  placesChangedRef.current = handlePlacesChanged;

  const { lat: centerLat, lng: centerLng } = police.mapCenter;

  useEffect(() => {
    if (!placesLibrary || !searchInputRef.current) return;

    const searchBox = new placesLibrary.SearchBox(searchInputRef.current, {
      bounds: {
        south: centerLat - 0.05,
        west: centerLng - 0.05,
        north: centerLat + 0.05,
        east: centerLng + 0.05,
      },
    });
    searchBoxRef.current = searchBox;

    const listener = searchBox.addListener("places_changed", () =>
      placesChangedRef.current(),
    );

    return () => {
      listener.remove();
      google.maps.event.clearInstanceListeners(searchBox);
      searchBoxRef.current = null;
    };
  }, [placesLibrary, centerLat, centerLng]);

  return (
    <>
      {apiIsLoaded ? (
        <div style={{ width: "100%" }}>
          {touched.googleMapsPlace && errors.googleMapsPlace && (
            <Typography sx={{ color: "red" }}>
              {errors.googleMapsPlace}
            </Typography>
          )}

          <input
            ref={searchInputRef}
            value={addressText}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setAddressText(event.target.value);
            }}
            onKeyDown={(e) => {
              e.key === "Enter" && e.preventDefault();
            }}
            type="text"
            name="googleMapsPlace"
            placeholder="Search address"
            onBlur={handleBlur}
            style={{
              boxSizing: `border-box`,
              border:
                touched.googleMapsPlace && errors.googleMapsPlace
                  ? `1px solid red`
                  : `1px solid transparent`,
              width: "100%",
              height: `40px`,
              padding: `0 12px`,
              borderRadius: `3px`,
              boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
              fontSize: `14px`,
              marginBottom: "4px",
              outline: `none`,
              textOverflow: `ellipses`,
            }}
          />

          {showUnit && (
            <div>
              <input
                type="text"
                className="sd-input sd-text"
                style={{
                  marginTop: "0.5rem",
                }}
                placeholder="Apt, Unit, etc. (optional)"
                value={values.unit}
                onChange={(e) => {
                  setFieldValue("unit", e.target.value);
                }}
              />
            </div>
          )}

          <Map
            colorScheme={mapColorScheme}
            center={camera.center}
            zoom={camera.zoom}
            onCameraChanged={(event: MapCameraChangedEvent) =>
              setCamera({
                center: event.detail.center,
                zoom: event.detail.zoom,
              })
            }
            style={{ height: "412px", width: "100%" }}
            streetViewControl={false}
            tilt={0}
          >
            <Marker position={location} />
          </Map>
          {errors.googleMapsPlace && (
            <div className="error">{errors.googleMapsPlace}</div>
          )}
        </div>
      ) : (
        <div>Map Loading ...</div>
      )}
    </>
  );
};

export default MapSearch;
